import React, { useState, useEffect } from 'react';
import { fetchSubcounties } from '../services/subcountyService';
import { createUser, updateUser, getAllUsers } from '../services/userService';

const UserCreationForm = ({ initialData, onSuccess }) => {
    const [subcounties, setSubcounties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role: 'Viewer',
        status: 'Active',
        sub_county_id: ''
    });

    const roles = [
        'Operations Admin', 'Sub County Revenue Officer', 
        'Management Team', 'Executive', 'Board Member', 'Viewer'
    ];

    /*
    useEffect(() => {
        fetchSubcounties().then(setSubcounties);
        if (initialData) {
            setFormData({ ...initialData, password: '' }); // Don't load hash into password field
        }
    }, [initialData]);
    */
   useEffect(() => {
        const loadMetadata = async () => {
            try {
                // Fetch sub-counties and all users in parallel
                const [allSubcounties, allUsers] = await Promise.all([
                    fetchSubcounties(),
                    getAllUsers()
                ]);

                // 1. Identify sub-county IDs already assigned to a 'Sub County Revenue Officer'
                // We exclude the current user (if editing) so their own sub-county remains in their list
                const assignedIds = allUsers
                    .filter(u => 
                        u.role === 'Sub County Revenue Officer' && 
                        u.sub_county_id && 
                        u.id !== initialData?.id
                    )
                    .map(u => Number(u.sub_county_id));

                // 2. Filter list: show sub-counties that do NOT have an officer assigned
                const availableSubcounties = allSubcounties.filter(
                    sc => !assignedIds.includes(Number(sc.id))
                );

                setSubcounties(availableSubcounties);
            } catch (err) {
                console.error("Failed to load form metadata:", err);
            }
        };

        loadMetadata();

        if (initialData) {
            setFormData({ ...initialData, password: '' });
        }
    }, [initialData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.role === 'Sub County Revenue Officer' && !formData.sub_county_id) {
            return alert("Please select a Sub-County for this role.");
        }
        if (formData.role !== 'Sub County Revenue Officer') {
            formData.sub_county_id = null; // Clear sub-county if role doesn't require it
        }

        setLoading(true);
        try {
            if (initialData) {
                await updateUser(initialData.id, formData);
            } else {
                await createUser(formData);
            }
            alert(initialData ? "User updated!" : "User created successfully!");
            onSuccess();
        } catch (err) {
            alert(err.response?.data?.message || "Error saving user.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-8 rounded-2xl border shadow-sm max-w-4xl mx-auto">
            <h3 className="text-lg font-bold mb-6 text-gray-700">
                {initialData ? 'Update User Account' : 'Register New System User'}
            </h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                    <label className="text-sm font-semibold text-gray-600">User Full Name</label>
                    <input type="text" required className="w-full border rounded-xl p-3" 
                        value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} />
                </div>
                <div className="space-y-1">
                    <label className="text-sm font-semibold text-gray-600">Email Address</label>
                    <input type="email" required className="w-full border rounded-xl p-3" 
                        value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                </div>
                
                {!initialData && (
                    <div className="space-y-1">
                        <label className="text-sm font-semibold text-gray-600">Temporary Password</label>
                        <input type="password" required className="w-full border rounded-xl p-3" 
                            value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                    </div>
                )}

                <div className="space-y-1">
                    <label className="text-sm font-semibold text-gray-600">System Role</label>
                    <select className="w-full border rounded-xl p-3" value={formData.role}
                        onChange={e => setFormData({...formData, role: e.target.value})}>
                        {roles.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                <div className="space-y-1">
                    <label className="text-sm font-semibold text-gray-600">Sub-County Assignment</label>
                    <select className="w-full border rounded-xl p-3" value={formData.sub_county_id}
                        onChange={e => setFormData({...formData, sub_county_id: e.target.value})}>
                        <option value="">N/A (HQ/Executive)</option>
                        {subcounties.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                    </select>
                </div>

                <div className="md:col-span-2 pt-4">
                    <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold shadow-lg">
                        {loading ? 'Processing...' : initialData ? 'Update Account' : 'Create User'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserCreationForm;